import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { SlidersHorizontal, Moon } from "lucide-react";

const CATS = [
  { key: "donations", label: "New donations", hint: "When a supporter gives to your campaign" },
  { key: "approvals", label: "Campaign approvals", hint: "When a campaign is approved or rejected" },
  { key: "payouts", label: "Payouts", hint: "When a payout is released to your bank" },
  { key: "milestones", label: "Momentum & milestones", hint: "When a campaign hits 25/50/100% or gets a big gift" },
  { key: "updates", label: "Updates you follow", hint: "When a campaign you follow posts an update" },
  { key: "digest", label: "Weekly summary", hint: "A Monday recap of donations & momentum" },
];

const US_ZONES = [
  { v: "America/New_York", l: "Eastern" },
  { v: "America/Chicago", l: "Central" },
  { v: "America/Denver", l: "Mountain" },
  { v: "America/Los_Angeles", l: "Pacific" },
  { v: "America/Anchorage", l: "Alaska" },
  { v: "Pacific/Honolulu", l: "Hawaii" },
];

const DEFAULT_QH = { enabled: false, start: "22:00", end: "07:00", tz: "America/New_York" };

export function NotificationPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [qh, setQh] = useState(DEFAULT_QH);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/push/preferences")
      .then((r) => {
        setPrefs(r.data);
        setQh({ ...DEFAULT_QH, ...(r.data.quiet_hours || {}) });
      })
      .catch(() => setPrefs(null));
  }, []);

  if (!prefs) return null;

  const toggle = async (key) => {
    const nextVal = !prefs[key];
    setPrefs((p) => ({ ...p, [key]: nextVal }));
    setSaving(true);
    try {
      const { data } = await api.put("/push/preferences", { [key]: nextVal });
      setPrefs((p) => ({ ...data, quiet_hours: p.quiet_hours }));
    } catch (e) {
      toast.error("Could not save that preference.");
      setPrefs((p) => ({ ...p, [key]: !nextVal }));
    } finally {
      setSaving(false);
    }
  };

  const saveQh = async (next) => {
    const prev = qh;
    setQh(next);
    try {
      const { data } = await api.put("/push/quiet-hours", next);
      setQh({ ...DEFAULT_QH, ...data });
    } catch (e) {
      toast.error("Could not save quiet hours.");
      setQh(prev);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-ff-border bg-white p-5 shadow-sm" data-testid="notification-preferences">
      <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue">
        <SlidersHorizontal className="h-4 w-4 text-ff-blue" /> What to notify me about
      </div>
      <p className="mt-0.5 text-xs text-ff-medium">Applies to push alerts and your weekly email summary.</p>

      <div className="mt-3 space-y-2.5">
        {CATS.map((c) => (
          <label
            key={c.key}
            data-testid={`pref-${c.key}`}
            className="flex cursor-pointer items-start gap-3 rounded-lg border border-ff-border p-3 transition-colors duration-200 hover:border-ff-blue"
          >
            <Checkbox
              checked={!!prefs[c.key]}
              onCheckedChange={() => toggle(c.key)}
              disabled={saving}
              data-testid={`pref-toggle-${c.key}`}
              className="mt-0.5"
            />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ff-blue">{c.label}</span>
              <span className="block text-xs text-ff-medium">{c.hint}</span>
            </span>
          </label>
        ))}
      </div>

      <div className="mt-4 border-t border-ff-border pt-4">
        <label className="flex cursor-pointer items-start gap-3" data-testid="quiet-hours-row">
          <Checkbox
            checked={!!qh.enabled}
            onCheckedChange={(v) => saveQh({ ...qh, enabled: !!v })}
            data-testid="quiet-hours-enabled"
            className="mt-0.5"
          />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-ff-blue">
              <Moon className="h-3.5 w-3.5 text-ff-blue" /> Quiet hours
            </span>
            <span className="block text-xs text-ff-medium">
              Pause push alerts overnight. You'll still see everything in email &amp; your dashboard.
            </span>
          </span>
        </label>

        {qh.enabled && (
          <div className="mt-3 grid grid-cols-2 gap-3" data-testid="quiet-hours-config">
            <label className="text-xs font-semibold text-ff-medium">
              From
              <input
                type="time"
                value={qh.start}
                onChange={(e) => setQh((q) => ({ ...q, start: e.target.value }))}
                onBlur={() => saveQh(qh)}
                data-testid="quiet-start"
                className="mt-1 w-full rounded-lg border border-ff-border px-3 py-2 text-sm text-ff-blue focus:border-ff-blue focus:outline-none"
              />
            </label>
            <label className="text-xs font-semibold text-ff-medium">
              To
              <input
                type="time"
                value={qh.end}
                onChange={(e) => setQh((q) => ({ ...q, end: e.target.value }))}
                onBlur={() => saveQh(qh)}
                data-testid="quiet-end"
                className="mt-1 w-full rounded-lg border border-ff-border px-3 py-2 text-sm text-ff-blue focus:border-ff-blue focus:outline-none"
              />
            </label>
            <label className="col-span-2 text-xs font-semibold text-ff-medium">
              Time zone
              <select
                value={qh.tz}
                onChange={(e) => saveQh({ ...qh, tz: e.target.value })}
                data-testid="quiet-tz"
                className="mt-1 w-full rounded-lg border border-ff-border px-3 py-2 text-sm text-ff-blue focus:border-ff-blue focus:outline-none"
              >
                {US_ZONES.map((z) => (
                  <option key={z.v} value={z.v}>{z.l}</option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationPreferences;
